import { fabric } from 'fabric';

export interface CanvasDimensions {
  width: number;
  height: number;
}

const COORDINATE_KEYS_XY = ['left', 'top', 'width', 'height', 'rx', 'ry', 'x1', 'y1', 'x2', 'y2'];

export const isValidCanvasDimensions = (dimensions: CanvasDimensions): boolean =>
  dimensions.width > 0 && dimensions.height > 0;

export function normalizeObjectCoordinates(
  object: fabric.Object | Record<string, any>,
  dimensions: CanvasDimensions,
): void {
  scaleObjectCoordinates(object, 1 / dimensions.width, 1 / dimensions.height);
}

export function denormalizeObjectCoordinates(
  object: fabric.Object | Record<string, any>,
  dimensions: CanvasDimensions,
): void {
  scaleObjectCoordinates(object, dimensions.width, dimensions.height);
}

export function rescaleObjectBetweenCanvasSizes(
  object: fabric.Object,
  fromDimensions: CanvasDimensions,
  toDimensions: CanvasDimensions,
): void {
  if (!isValidCanvasDimensions(fromDimensions) || !isValidCanvasDimensions(toDimensions)) {
    return;
  }

  const scaleX = toDimensions.width / fromDimensions.width;
  const scaleY = toDimensions.height / fromDimensions.height;
  scaleObjectCoordinates(object, scaleX, scaleY);
}

function scaleObjectCoordinates(
  object: fabric.Object | Record<string, any>,
  scaleX: number,
  scaleY: number,
): void {
  if (!Number.isFinite(scaleX) || !Number.isFinite(scaleY) || scaleX <= 0 || scaleY <= 0) {
    return;
  }

  // Fabric clones can keep nested geometry references (e.g. path/points) from the source object.
  // Detach those first so normalization never mutates the live object on the local canvas.
  detachMutableGeometryReferences(object as Record<string, any>);

  scaleGenericCoordinates(object as Record<string, any>, scaleX, scaleY);
  scalePointLikeProperty(object as Record<string, any>, 'pathOffset', scaleX, scaleY);
  scalePointsProperty(object as Record<string, any>, 'points', scaleX, scaleY);
  scaleSvgPathCommands(object as Record<string, any>, scaleX, scaleY);
}

function detachMutableGeometryReferences(object: Record<string, any>): void {
  if (Array.isArray(object.points)) {
    object.points = object.points.map((point: any) => {
      if (!point || typeof point !== 'object') {
        return point;
      }
      return new fabric.Point(point.x, point.y);
    });
  }

  if (object.pathOffset && typeof object.pathOffset === 'object') {
    object.pathOffset = new fabric.Point(object.pathOffset.x, object.pathOffset.y);
  }

  if (Array.isArray(object.path)) {
    object.path = object.path.map((command: any) => {
      if (!Array.isArray(command)) {
        return command;
      }
      return [...command];
    });
  }
}

function scaleGenericCoordinates(
  object: Record<string, any>,
  scaleX: number,
  scaleY: number,
): void {
  for (const key of COORDINATE_KEYS_XY) {
    const value = object[key];
    if (typeof value !== 'number') {
      continue;
    }
    object[key] = value * (isYKey(key) ? scaleY : scaleX);
  }
}

function isYKey(key: string): boolean {
  return (
    key === 'y' || key === 'y1' || key === 'y2' || key === 'top' || key === 'height' || key === 'ry'
  );
}

function scalePointLikeProperty(
  object: Record<string, any>,
  key: string,
  scaleX: number,
  scaleY: number,
): void {
  const value = object[key];
  if (!value || typeof value !== 'object') {
    return;
  }

  if (typeof value.x === 'number') {
    value.x *= scaleX;
  }
  if (typeof value.y === 'number') {
    value.y *= scaleY;
  }
}

function scalePointsProperty(
  object: Record<string, any>,
  key: string,
  scaleX: number,
  scaleY: number,
): void {
  const points = object[key];
  if (!Array.isArray(points)) {
    return;
  }

  for (const point of points) {
    if (!point || typeof point !== 'object') {
      continue;
    }
    if (typeof point.x === 'number') {
      point.x *= scaleX;
    }
    if (typeof point.y === 'number') {
      point.y *= scaleY;
    }
  }
}

function scaleSvgPathCommands(object: Record<string, any>, scaleX: number, scaleY: number): void {
  const path = object.path;
  if (!Array.isArray(path)) {
    return;
  }

  for (const command of path) {
    if (!Array.isArray(command) || command.length === 0) {
      continue;
    }
    const action = typeof command[0] === 'string' ? command[0].toUpperCase() : '';

    switch (action) {
      case 'M':
      case 'L':
      case 'T':
      case 'Q':
      case 'S':
      case 'C':
        scaleCommandPairCoords(command, 1, scaleX, scaleY);
        break;
      case 'A':
        // A rx ry x-axis-rotation large-arc-flag sweep-flag x y
        scaleCommandIndex(command, 1, scaleX);
        scaleCommandIndex(command, 2, scaleY);
        scaleCommandIndex(command, 6, scaleX);
        scaleCommandIndex(command, 7, scaleY);
        break;
      case 'H':
        scaleCommandIndex(command, 1, scaleX);
        break;
      case 'V':
        scaleCommandIndex(command, 1, scaleY);
        break;
      default:
        break;
    }
  }
}

function scaleCommandPairCoords(
  command: unknown[],
  startIndex: number,
  scaleX: number,
  scaleY: number,
): void {
  for (let i = startIndex; i < command.length; i += 2) {
    scaleCommandIndex(command, i, scaleX);
    scaleCommandIndex(command, i + 1, scaleY);
  }
}

function scaleCommandIndex(command: unknown[], index: number, scale: number): void {
  const value = command[index];
  if (typeof value !== 'number') {
    return;
  }
  command[index] = value * scale;
}
